import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, from, fromEvent, merge, timer } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

/**
 * Detects when a newer build of the app has been deployed by polling the static `version.json`
 * emitted at build time (see scripts/generate-version.mjs). The first successful fetch captures
 * the "booted" version; any later fetch with a different version latches `updateAvailable$` to
 * true. Callers decide what to do with that (banner, gated reload — see AppComponent).
 *
 * Failures (offline, or a 404 during `ng serve` where version.json isn't emitted) are ignored:
 * detection is best-effort and must never disrupt the running app.
 */
@Injectable({ providedIn: 'root' })
export class VersionService {
  // Relative URL so it resolves against the per-locale <base href> (e.g. /en-US/version.json).
  private readonly versionUrl = 'version.json';
  private readonly pollIntervalMs = 10 * 60 * 1000; // 10 minutes

  private bootVersion: string | null = null;
  private pollSub: Subscription | null = null;

  private readonly updateAvailableSubject = new BehaviorSubject<boolean>(false);
  public readonly updateAvailable$: Observable<boolean> = this.updateAvailableSubject.asObservable();

  /**
   * Begin periodic version checks. Also re-checks whenever the tab regains focus, so a user
   * returning after a while gets a prompt check rather than waiting out the interval. Safe to
   * call more than once — subsequent calls are no-ops.
   */
  startPolling(): void {
    if (this.pollSub) return;

    // Poll on an interval (firing immediately) and additionally whenever the tab regains focus, so
    // a returning user gets a prompt check rather than waiting out the interval. switchMap keeps at
    // most one check in flight. Mirrors the timer(0, INTERVAL).pipe(switchMap(...)) polling idiom
    // used elsewhere (e.g. analysis-list-item.component.ts); one subscription tears it all down.
    const tabVisible$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible')
    );
    this.pollSub = merge(timer(0, this.pollIntervalMs), tabVisible$)
      .pipe(switchMap(() => from(this.checkForUpdate())))
      .subscribe();
  }

  stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  /** Fetch version.json and compare against the booted version. */
  async checkForUpdate(): Promise<void> {
    try {
      const response = await fetch(this.versionUrl, { cache: 'no-store' });
      // When version.json is absent the SPA fallback serves index.html with HTTP 200, so `ok`
      // alone isn't enough — require a JSON content-type before trusting/parsing the body.
      const contentType = response.headers.get('content-type') ?? '';
      if (!response.ok || !contentType.includes('application/json')) return;

      const data = (await response.json()) as { version?: string };
      const version = data?.version;
      if (!version) return;

      if (this.bootVersion === null) {
        this.bootVersion = version;
      } else if (version !== this.bootVersion && !this.updateAvailableSubject.value) {
        this.updateAvailableSubject.next(true);
      }
    } catch {
      // Offline or version.json unavailable (e.g. local `ng serve`) — ignore, try again next tick.
    }
  }
}
